import React, { useEffect, useMemo, useState } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, Polyline, useMap } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import 'leaflet.heat';
import { Flame, MapPin, Route as RouteIcon } from 'lucide-react';

export interface PontoVisita {
    paradaId: string;
    lat: number;
    lng: number;
    titulo: string;
    vendedorNome: string;
    dataVisita?: string | null;
    statusLead: string;
    kwh: number;
    cidade?: string | null;
}

export interface TrajetoRota {
    rotaId: string;
    data: string;
    vendedorNome: string;
    coords: Array<[number, number]>;
}

const CORES_TRAJETO = ['#3b82f6', '#a855f7', '#f97316', '#14b8a6', '#ef4444', '#eab308', '#6366f1'];

const STATUS_LABEL: Record<string, string> = {
    em_andamento: 'Em andamento',
    simulacao_realizada: 'Simulação realizada',
    termo_enviado: 'Termo enviado',
    termo_analise: 'Termo em análise',
    termo_aprovado: 'Termo aprovado',
    termo_assinado: 'Termo assinado',
    termo_reprovado: 'Termo reprovado',
    em_pausa: 'Em pausa',
    ganho: 'Ganho',
    perdido: 'Perdido',
};

/** Camada de mapa de calor (leaflet.heat) das visitas realizadas. */
const HeatLayer: React.FC<{ pontos: PontoVisita[]; ativo: boolean }> = ({ pontos, ativo }) => {
    const map = useMap();

    // O container pode montar dentro de aba/painel recém-exibido: sem invalidateSize
    // o canvas do heat fica com dimensão errada e a camada "some" ou desloca.
    useEffect(() => {
        const t = setTimeout(() => map.invalidateSize(), 150);
        return () => clearTimeout(t);
    }, [map]);

    // Chave estável evita recriar a camada a cada render com o mesmo conjunto de pontos
    const chave = useMemo(() => pontos.map(p => p.paradaId).join('|'), [pontos]);

    useEffect(() => {
        if (!ativo || pontos.length === 0) return;
        const layer = (L as any).heatLayer(
            pontos.map(p => [p.lat, p.lng, 0.7]),
            { radius: 30, blur: 20, maxZoom: 12, minOpacity: 0.35 }
        );
        layer.addTo(map);
        return () => { try { map.removeLayer(layer); } catch (_) { /* mapa já desmontado */ } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chave, ativo, map]);
    return null;
};

/** Ajusta o enquadramento aos pontos do período. */
const FitBounds: React.FC<{ pontos: PontoVisita[] }> = ({ pontos }) => {
    const map = useMap();
    useEffect(() => {
        if (pontos.length === 0) return;
        const bounds = L.latLngBounds(pontos.map(p => [p.lat, p.lng] as [number, number]));
        map.fitBounds(bounds.pad(0.25), { maxZoom: 12 });
    }, [pontos.length, map]);
    return null;
};

interface MapaCampoProps {
    pontos: PontoVisita[];
    trajetos: TrajetoRota[];
}

/** Mapa de Inteligência Comercial (Rotas v2 Fase 4): visitas, calor e trajetos do período. */
const MapaCampo: React.FC<MapaCampoProps> = ({ pontos, trajetos }) => {
    const [mostraPontos, setMostraPontos] = useState(true);
    const [mostraCalor, setMostraCalor] = useState(false);
    const [mostraTrajetos, setMostraTrajetos] = useState(true);

    const toggles = useMemo(() => ([
        { label: 'Visitas', icon: <MapPin size={12} />, ativo: mostraPontos, set: setMostraPontos },
        { label: 'Mapa de Calor', icon: <Flame size={12} />, ativo: mostraCalor, set: setMostraCalor },
        { label: 'Trajetos', icon: <RouteIcon size={12} />, ativo: mostraTrajetos, set: setMostraTrajetos },
    ]), [mostraPontos, mostraCalor, mostraTrajetos]);

    return (
        <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm p-4">
            <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
                <div>
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white">Mapa de Inteligência Comercial</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Replay da operação no período · {pontos.length} visitas georreferenciadas · {trajetos.length} trajetos</p>
                </div>
                <div className="flex items-center gap-1.5">
                    {toggles.map(t => (
                        <button
                            key={t.label}
                            onClick={() => t.set(v => !v)}
                            className={`flex items-center gap-1.5 px-3 py-1.5 text-[11px] font-bold rounded-full transition-all ${t.ativo
                                ? 'bg-primary text-white shadow-sm'
                                : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`}
                        >
                            {t.icon} {t.label}
                        </button>
                    ))}
                </div>
            </div>

            <div className="h-[420px] rounded-xl overflow-hidden border border-gray-200 dark:border-gray-700 shadow-inner relative z-0">
                {pontos.length === 0 && trajetos.length === 0 ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-800 text-gray-400">
                        <MapPin size={36} className="mb-2 opacity-30" />
                        <p className="text-sm">Nenhuma visita georreferenciada no período.</p>
                    </div>
                ) : (
                    <MapContainer center={[-16.68, -49.25]} zoom={7} scrollWheelZoom className="h-full w-full">
                        <TileLayer
                            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                        />
                        <FitBounds pontos={pontos} />
                        <HeatLayer pontos={pontos} ativo={mostraCalor} />

                        {mostraTrajetos && trajetos.map((t, i) => (
                            <Polyline
                                key={t.rotaId}
                                positions={t.coords}
                                pathOptions={{ color: CORES_TRAJETO[i % CORES_TRAJETO.length], weight: 3, opacity: 0.6, dashArray: '6 8' }}
                            >
                                <Popup>
                                    <p className="font-semibold text-xs">Rota de {new Date(t.data + 'T12:00:00').toLocaleDateString('pt-BR')}</p>
                                    <p className="text-xs text-gray-600">{t.vendedorNome} · {t.coords.length} paradas</p>
                                </Popup>
                            </Polyline>
                        ))}

                        {mostraPontos && pontos.map(p => (
                            <CircleMarker
                                key={p.paradaId}
                                center={[p.lat, p.lng]}
                                radius={7}
                                pathOptions={{ color: '#fff', weight: 2, fillColor: '#10b981', fillOpacity: 0.95 }}
                            >
                                <Popup>
                                    <p className="font-semibold border-b pb-1 mb-1.5">{p.titulo}</p>
                                    <p className="text-xs text-gray-600 mb-0.5"><b>Responsável:</b> {p.vendedorNome}</p>
                                    <p className="text-xs text-gray-600 mb-0.5"><b>Visita:</b> {p.dataVisita ? new Date(p.dataVisita).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</p>
                                    <p className="text-xs text-gray-600 mb-0.5"><b>Status do lead:</b> {STATUS_LABEL[p.statusLead] || p.statusLead}</p>
                                    <p className="text-xs text-gray-600"><b>Potencial:</b> {Math.round(p.kwh).toLocaleString('pt-BR')} kWh</p>
                                </Popup>
                            </CircleMarker>
                        ))}
                    </MapContainer>
                )}
            </div>
        </div>
    );
};

export default MapaCampo;
